class CarDrawerViewManager {
  #container;
  #mainContent;
  #currentView = 'list';
  #isTransitioning = false;
  #transitionController = null;
  #allowedTransitions = new Map([
    ['list', ['manage', 'add']],
    ['manage', ['list']],
    ['add', ['list', 'selector']],
    ['selector', ['add']]
  ]);

  constructor(container) {
    this.#container = container;
    this.#mainContent = container.querySelector('.car-drawer__main-content');

    this.#currentView = this.#mainContent?.dataset?.view || 'list';

    const header = container.querySelector('.menu-drawer__header');
    if (header) {
      header.dataset.drawerView = this.#currentView;
    }

    this.#loadConfiguration();
  }

  #loadConfiguration() {
    try {
      const transitionsData = this.#mainContent?.dataset?.allowedTransitions;
      if (transitionsData) {
        const parsed = JSON.parse(transitionsData);
        this.#allowedTransitions = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.warn('Using default transition rules:', error.message);
    }
  }
  #canTransitionTo(targetView) {
    return this.#allowedTransitions.get(this.#currentView)?.includes(targetView) ?? false;
  }
  async switchTo(targetView, options = {}) {
    if (targetView === this.#currentView) return Promise.resolve(false);

    if (!options.force && !this.#canTransitionTo(targetView)) {
      return Promise.resolve(false);
    }

    if (this.#isTransitioning) {
      this.#transitionController?.abort();
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      this.#transitionController = new AbortController();
      const signal = this.#transitionController.signal;
      let timeoutId = null;

      this.#isTransitioning = true;

      timeoutId = setTimeout(() => {
        this.#cleanup(false, resolve, timeoutId);
      }, 5000);

      if (signal.aborted) {
        this.#cleanup(false, resolve, timeoutId);
        return;
      }

      signal.addEventListener('abort', () => {
        this.#cleanup(false, resolve, timeoutId);
      });

      this.#dispatchEvent('beforeViewChange', {
        from: this.#currentView,
        to: targetView
      });

      const previousView = this.#currentView;
      this.#mainContent.dataset.view = targetView;
      this.#currentView = targetView;

      const header = this.#container.querySelector('.menu-drawer__header');
      header && (header.dataset.drawerView = targetView);

      this.#mainContent.classList.add('car-drawer__main-content--transitioning');

      setTimeout(() => {
        if (!signal.aborted) {
          this.#mainContent.classList.remove('car-drawer__main-content--transitioning');

          this.#dispatchEvent('afterViewChange', {
            from: previousView,
            to: targetView
          });

          this.#cleanup(true, resolve, timeoutId);
        }
      }, 300);
    });
  }

  #cleanup(success, resolve, timeoutId) {
    if (timeoutId) clearTimeout(timeoutId);

    this.#isTransitioning = false;
    this.#transitionController = null;
    resolve(success);
  }

  async goToList() {
    const result = await this.switchTo('list', { force: true });
    if (result) {
      this.updateCarCount();
    }
    return result;
  }

  async goToAdd() {
    return this.switchTo('add');
  }

  async goToManage() {
    return this.switchTo('manage');
  }

  async goToSelector() {
    return this.switchTo('selector');
  }

  async goBack() {
    const backTransitions = {
      'manage': 'list',
      'add': 'list',
      'selector': 'add'
    };

    const targetView = backTransitions[this.#currentView];
    return targetView ? this.switchTo(targetView, { force: true }) : Promise.resolve(false);
  }

  #dispatchEvent(eventName, detail) {
    const event = new CustomEvent(`carDrawer:${eventName}`, {
      detail,
      bubbles: true,
      cancelable: true
    });

    this.#container?.dispatchEvent(event);
  }

  updateCarCount() {
    const actionsContainer = this.#container.querySelector('[data-view-actions="list"]');
    const vehicleItems = this.#container.querySelectorAll('.car-drawer__vehicle-item');

    if (actionsContainer) {
      actionsContainer.setAttribute('data-car-count', vehicleItems.length);
    }
  }

  get currentView() {
    return this.#currentView;
  }

  get isTransitioning() {
    return this.#isTransitioning;
  }

  cancel() {
    if (this.#transitionController && !this.#transitionController.signal.aborted) {
      this.#transitionController.abort();
      return true;
    }
    return false;
  }

  destroy() {
    this.cancel();
    this.#allowedTransitions.clear();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('#car-drawer-dialog');
  if (!container) return;

  const viewManager = new CarDrawerViewManager(container);

  container.addEventListener('click', async (event) => {
    const action = event.target.closest('[data-action]')?.dataset?.action;
    if (!action) return;

    event.preventDefault();

    const actionResult = await (async () => {
      switch (action) {
        case 'manage-cars': return viewManager.goToManage();
        case 'add-first-car': return viewManager.goToAdd();
        case 'cancel-manage': return viewManager.goBack();
        case 'back-from-add': return viewManager.goBack();
        case 'back-from-selector': return viewManager.goBack();
        case 'show-products':
          return Promise.resolve(true);
        case 'submit-car':
          const submitResult = await viewManager.goToList();
          if (submitResult) {
            viewManager.updateCarCount();
          }
          return Promise.resolve(true);
        default:
          return Promise.resolve(false);
      }
    })();
  });
});
